import { Box, Button, TextField, Typography } from '@mui/material';
import { ENavigationButton } from 'components/Buttons/NavigationButtons';
import { getURLFromSearchCriteria } from 'components/Search/search.helpers';
import { useSnackbar } from 'notistack';
import { ICurationStubStudy, IImport, EImportMode } from 'pages/Curation/Curation.types';
import { SELECTED_CURATION_STEP_LOCAL_STORAGE_KEY_SUFFIX } from 'pages/Curation/context/CurationBoardGroupsContext';
import {
    automaticallyResolveDuplicates,
    createDuplicateMap,
    hasDuplicates,
} from 'pages/CurationImport/CurationImport.helpers';
import {
    useCreateNewCurationImport,
    useProjectCurationColumns,
    useProjectId,
    useUpdateCurationColumns,
} from 'stores/projects/ProjectStore';
import { defaultExclusionTags } from 'stores/projects/ProjectStore.consts';
import { SearchCriteria } from 'pages/Study/Study.types';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import CurationImportStyles from '../CurationImport.styles';
import ImportFinalizeReview from './ImportFinalizeReview';

const generateDefaultImportName = (
    importMode: EImportMode,
    stubs: ICurationStubStudy[],
    searchCriteria: SearchCriteria | undefined,
    fileName: string | undefined
) => {
    switch (importMode) {
        case EImportMode.NEUROSTORE_IMPORT: {
            let finalImportName = '';

            if (searchCriteria?.genericSearchStr) {
                finalImportName = `${searchCriteria.genericSearchStr}`;
            }

            if (searchCriteria?.nameSearch) {
                const nameStrSegment = searchCriteria.nameSearch;
                finalImportName =
                    finalImportName.length > 0
                        ? `${finalImportName} with name "${nameStrSegment}"`
                        : `name "${nameStrSegment}"`;
            }

            if (searchCriteria?.journalSearch) {
                const journalStrSegment = searchCriteria.journalSearch;
                finalImportName =
                    finalImportName.length > 0
                        ? `${finalImportName} in journal "${journalStrSegment}"`
                        : `journal "${journalStrSegment}"`;
            }

            if (searchCriteria?.authorSearch) {
                const authorStrSegment = searchCriteria.authorSearch;
                finalImportName =
                    finalImportName.length > 0
                        ? `${finalImportName} by author "${authorStrSegment}"`
                        : `author "${authorStrSegment}"`;
            }

            if (searchCriteria?.descriptionSearch) {
                const descriptionStrSegment = searchCriteria.descriptionSearch;
                finalImportName =
                    finalImportName.length > 0
                        ? `${finalImportName} with description "${descriptionStrSegment}"`
                        : `description "${descriptionStrSegment}"`;
            }

            return finalImportName;
        }
        case EImportMode.FILE_IMPORT: {
            const source = stubs[0].identificationSource; // this is safe because we know we must have at least one stub
            const finalImportName = fileName ? `${fileName} from ${source.label}` : source.label;
            return finalImportName;
        }
        case EImportMode.SLEUTH_IMPORT: {
            return fileName ?? 'Sleuth Import';
        }
        case EImportMode.MANUAL_CREATE:
            return stubs[0].title || '';
        case EImportMode.PUBMED_IMPORT:
            if (fileName) {
                return `${fileName}`;
            } else {
                const pmids = stubs.reduce((acc, curr, index) => {
                    if (index === 0) return curr.pmid;
                    return `${acc}, ${curr.pmid}`;
                }, '');

                return pmids;
            }
    }
};

const ImportFinalize = ({  onNavigate, stubs, unimportedStubs, importMode, searchCriteria, fileName  }: {
    importMode: EImportMode;
    onNavigate: (button: ENavigationButton) => void;
    stubs: ICurationStubStudy[];
    unimportedStubs: string[];
    searchCriteria: SearchCriteria | undefined;
    fileName: string | undefined;
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const updateCurationColumns = useUpdateCurationColumns();
    const columns = useProjectCurationColumns();
    const createNewImport = useCreateNewCurationImport();
    const projectId = useProjectId();

    const [importName, setImportName] = useState(
        generateDefaultImportName(importMode, stubs, searchCriteria, fileName)
    );

    const onFinalizeImport = (importName: string) => {
        // 1. create new import. Add import to all stubs.
        const newImport: IImport = {
            id: uuid(),
            name: importName,
            errorsDuringImport:
                unimportedStubs.length > 0
                    ? `Unable to import studies with the following IDs: ${unimportedStubs.join(', ')}`
                    : undefined,
            importModeUsed: importMode,
            numImported: stubs.length,
            date: new Date().toISOString(),
            neurostoreSearchParams: searchCriteria ? getURLFromSearchCriteria(searchCriteria) : undefined,
        };
        createNewImport(newImport);
        stubs.forEach((stub) => {
            stub.importId = newImport.id;
        });

        // // 2. first find duplicates ONLY WITHIN THE IMPORT ITSELF. Label as duplicate.
        const duplicatesExistWithinImport = hasDuplicates(stubs);
        const deduplicatedStubs = duplicatesExistWithinImport ? automaticallyResolveDuplicates(stubs) : stubs;

        // // 3. Label the stubs in the import as duplicates automatically if we find existing stubs IN THE PROJECT
        const allStubsInProject = columns.reduce(
            (acc, curr) => [...acc, ...curr.stubStudies],
            [] as ICurationStubStudy[]
        );
        const { duplicateMapping } = createDuplicateMap(allStubsInProject);
        deduplicatedStubs.forEach((importedStub) => {
            if (importedStub.exclusionTag !== null) return;
            const formattedTitle = importedStub.title.toLocaleLowerCase().trim();
            if (importedStub.doi && duplicateMapping.has(importedStub.doi)) {
                importedStub.exclusionTag = defaultExclusionTags.duplicate.id;
            } else if (importedStub.pmid && duplicateMapping.has(importedStub.pmid)) {
                importedStub.exclusionTag = defaultExclusionTags.duplicate.id;
            } else if (importedStub.title && duplicateMapping.has(formattedTitle)) {
                importedStub.exclusionTag = defaultExclusionTags.duplicate.id;
            }
        });

        const updatedColumns = [...columns];
        updatedColumns[0] = {
            ...updatedColumns[0],
            stubStudies: [...deduplicatedStubs, ...updatedColumns[0].stubStudies],
        };

        updateCurationColumns(updatedColumns);
        enqueueSnackbar(`Added new import: ${importName}`, { variant: 'success' });

        // unfortunately we cannot use useCurationBoardGroups here because we are not in the context of the CurationBoardGroupsProvider...
        // in the future we should consider creating a base curation route and putting the curation search and curation page as children
        localStorage.removeItem(`${projectId}${SELECTED_CURATION_STEP_LOCAL_STORAGE_KEY_SUFFIX}`);

        onNavigate(ENavigationButton.NEXT);
    };

    const handleClickNext = () => {
        if (!importName) return;
        onFinalizeImport(importName);
        return;
    };

    return (
        <Box sx={{ paddingTop: '0.5rem' }}>
            <Box sx={{ margin: '1rem 0' }}>
                <Typography
                    gutterBottom
                    sx={{ fontWeight: 'bold', marginRight: '4px', display: 'inline' }}
                    variant="h6"
                >
                    Give your import a name:
                </Typography>
                <TextField
                    sx={{ width: '100%', marginTop: '0.5rem' }}
                    size="small"
                    value={importName}
                    onChange={(val) => setImportName(val.target.value)}
                />

                <ImportFinalizeReview stubs={stubs} unimportedStubs={unimportedStubs} />
            </Box>
            <Box sx={CurationImportStyles.actionsContainer}>
                {importMode !== EImportMode.SLEUTH_IMPORT && (
                    <Button
                        variant="outlined"
                        color="error"
                        sx={CurationImportStyles.actionsButton}
                        onClick={() => onNavigate(ENavigationButton.PREV)}
                    >
                        back
                    </Button>
                )}
                <Button
                    variant="contained"
                    sx={CurationImportStyles.actionsButton}
                    disableElevation
                    disabled={!importName || stubs.length === 0}
                    onClick={handleClickNext}
                >
                    Import
                </Button>
            </Box>
        </Box>
    );
};

export default ImportFinalize;
